import { useState } from 'react';

import clsxm from '@/lib/clsxm';

const R2 = 'https://media.easy-peasy.ai';

/** The product name, never split at its hyphen. */
export function Brand() {
  return <span className='whitespace-nowrap'>Easy-Peasy.AI</span>;
}

/*
 * Every image on this page is a pre-sized copy in R2 (bucket "images"),
 * served as-is by Cloudflare: render with `unoptimized` or a plain <img> so
 * Vercel never resizes or serves it.
 */
export const PHOTOS = {
  hero: `${R2}/landing/workshops/workshop-intro.webp`,
  session: `${R2}/landing/workshops/gallery/workshop-june-2025-1.webp`,
  marky: `${R2}/landing/about/marky.webp`,
} as const;

export type Persona = { title: string; text: string; image: string };

/** Illustrations of the people the workshop is for. */
export const PERSONAS: Persona[] = [
  {
    title: 'Busy entrepreneurs',
    text: 'Hand the routine work to AI and spend your hours on the business.',
    image: `${R2}/static/workshop/busy-entrepreneur-singapore-ai-workshop-v3.webp`,
  },
  {
    title: 'Influencers and creators',
    text: 'Make more content that sounds like you, in less time.',
    image: `${R2}/static/workshop/influencer-singapore-ai-workshop-v3.webp`,
  },
  {
    title: 'Marketing agencies',
    text: 'Turn one idea into posts, visuals and video ads for every client.',
    image: `${R2}/static/workshop/marketing-agency-singapore-ai-workshop-v3.webp`,
  },
  {
    title: 'Content executives',
    text: 'Keep the content calendar full without working late.',
    image: `${R2}/static/workshop/content-executive-singapore-ai-workshop-v3.webp`,
  },
  {
    title: 'Freelancers',
    text: 'Deliver more client work on time, with less effort on each piece.',
    image: `${R2}/static/workshop/freelancer-singapore-ai-workshop-v3.webp`,
  },
  {
    title: 'Growing companies',
    text: 'Give your teams the same tools and a shared way to use them.',
    image: `${R2}/static/workshop/enterprise-leader-singapore-ai-workshop-v3.webp`,
  },
];

export type GalleryPhoto = {
  src: string;
  title: string;
  date: string;
  width: number;
  height: number;
};

// Sizes of the R2 copies, so the wall reserves its space before they load.
const SIZES: Record<string, [number, number]> = {
  'workshop-47': [800, 534],
  'capitaland-workshop': [800, 600],
  'huttons-workshop': [800, 450],
  'google-june-2025-1': [800, 534],
  'workshop-june-2025-1': [800, 600],
  'google-june-2025-2': [800, 534],
  'workshop-june-2025-2': [800, 600],
  'google-june-2025-3': [800, 533],
  'workshop-march-2025': [800, 450],
  'workshop-march-3': [800, 600],
  'workshop-4': [640, 480],
  'ai-workshop-13': [640, 480],
  'ai-workshop-17': [640, 480],
  'workshop-11': [640, 480],
  'ai-workshop-14': [640, 480],
  'ai-workshop-15': [640, 480],
  'workshop-5': [640, 480],
  'ai-workshop-16': [640, 480],
  'ai-workshop-1': [800, 533],
  'workshop-1-1-1': [800, 533],
  'workshop-6': [640, 480],
  'ai-workshop-18': [640, 480],
};

const g = (file: string, title: string, date: string): GalleryPhoto => ({
  src: `${R2}/landing/workshops/gallery/${file}.webp`,
  title,
  date,
  width: SIZES[file][0],
  height: SIZES[file][1],
});

const PUBLIC = 'How To Make AI Work For You';

/** Photos from our sessions, newest first. */
export const GALLERY: GalleryPhoto[] = [
  g('workshop-47', PUBLIC, 'Oct 2025'),
  g('capitaland-workshop', 'Generative AI with CapitaLand', 'Sep 2025'),
  g('huttons-workshop', 'Generative AI with Huttons Group', 'Sep 2025'),
  g('google-june-2025-1', 'Agentic AI with Google', 'Jun 2025'),
  g('workshop-june-2025-1', PUBLIC, 'Jun 2025'),
  g('google-june-2025-2', 'Agentic AI with Google', 'Jun 2025'),
  g('workshop-june-2025-2', PUBLIC, 'Jun 2025'),
  g('google-june-2025-3', 'Agentic AI with Google', 'Jun 2025'),
  g('workshop-march-2025', 'Generative AI with Addison Institute', 'Mar 2025'),
  g('workshop-march-3', PUBLIC, 'Mar 2025'),
  g('workshop-4', PUBLIC, 'Nov 2024'),
  g('ai-workshop-13', PUBLIC, 'Nov 2024'),
  g('ai-workshop-17', PUBLIC, 'Nov 2024'),
  g('workshop-11', PUBLIC, 'Aug 2024'),
  g('ai-workshop-14', PUBLIC, 'Jul 2024'),
  g('ai-workshop-15', PUBLIC, 'Jul 2024'),
  g('workshop-5', PUBLIC, 'Jun 2024'),
  g('ai-workshop-16', PUBLIC, 'Jun 2024'),
  g('ai-workshop-1', PUBLIC, 'Apr 2024'),
  g('workshop-1-1-1', PUBLIC, 'Apr 2024'),
  g('workshop-6', PUBLIC, 'Apr 2024'),
  g('ai-workshop-18', 'Warm wishes from Japan', '2024'),
];

export type Video = {
  id: string;
  title: string;
  length: string;
  poster: string;
};

export const VIDEOS = {
  feedback: {
    id: 'nUX6DvUDsGg',
    title: 'What attendees said after the workshop',
    length: '1:47',
    poster: 'https://i.ytimg.com/vi/nUX6DvUDsGg/maxresdefault.jpg',
  },
  recap: {
    id: 'mnaa4thYeq4',
    title: 'Inside a How To Make AI Work For You session',
    length: '1:00',
    poster: 'https://i.ytimg.com/vi/mnaa4thYeq4/hqdefault.jpg',
  },
} satisfies Record<string, Video>;

export const VENUE = {
  name: 'JustCo, Cross Street Exchange',
  address: '18 Cross Street, #02-101, Singapore 048423',
  maps: 'https://www.google.com/maps/search/?api=1&query=Easy-Peasy.AI+Pte.+Ltd.%2C+18+Cross+Street%2C+Singapore+048423',
  embed:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.819!2d103.8470853!3d1.2835867!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da19479f576053%3A0x16f24919e90571c4!2sEasy-Peasy.AI%20Pte.%20Ltd.!5e0!3m2!1sen!2ssg!4v1781136000000!5m2!1sen!2ssg',
} as const;

/**
 * A YouTube video that loads nothing from YouTube until it's played: the
 * poster is a plain image and the player iframe only mounts on click.
 */
export function LiteVideo({
  video,
  className,
  priority = false,
}: {
  video: Video;
  className?: string;
  priority?: boolean;
}) {
  const [playing, setPlaying] = useState(false);

  return (
    <div
      className={clsxm(
        'relative aspect-video overflow-hidden bg-[#0d0c1f]',
        className,
      )}
    >
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
          title={video.title}
          className='absolute inset-0 h-full w-full'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          referrerPolicy='strict-origin-when-cross-origin'
          allowFullScreen
        />
      ) : (
        <button
          type='button'
          onClick={() => setPlaying(true)}
          className='group absolute inset-0 h-full w-full text-start focus-visible:outline-none'
          aria-label={`Play video: ${video.title} (${video.length})`}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={video.poster}
            alt=''
            loading={priority ? 'eager' : 'lazy'}
            decoding='async'
            className='absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]'
          />
          <span className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0' />
          <span className='absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-primary-600 shadow-[0_12px_30px_-8px_rgba(15,15,40,0.55)] transition duration-200 group-hover:scale-105 group-focus-visible:ring-4 group-focus-visible:ring-white/60'>
            <svg
              viewBox='0 0 24 24'
              className='ms-1 h-6 w-6'
              aria-hidden='true'
            >
              <path fill='currentColor' d='M8 5.14v13.72L19 12 8 5.14Z' />
            </svg>
          </span>
          <span className='absolute bottom-4 start-4 end-4 flex items-center justify-between gap-3 text-[13px] font-medium text-white'>
            <span className='truncate'>{video.title}</span>
            <span className='flex-none rounded-full bg-black/50 px-2 py-0.5 tabular-nums backdrop-blur'>
              {video.length}
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
